import { getDrawingTool } from "./drawingModel";

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function timestampMs(value) {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function nearestCandleX(candles, timestamp) {
  if (!Array.isArray(candles) || candles.length === 0) return null;
  const target = timestampMs(timestamp);
  if (target === null) return null;

  const candidates = candles
    .map((candle) => ({ candle, time: timestampMs(candle.timestamp) }))
    .filter((item) => item.time !== null && finiteNumber(item.candle.x) !== null);
  if (!candidates.length) return null;

  const first = candidates[0].time;
  const last = candidates[candidates.length - 1].time;
  if (target < first || target > last) return null;

  let nearest = candidates[0];
  candidates.forEach((item) => {
    if (Math.abs(item.time - target) < Math.abs(nearest.time - target)) {
      nearest = item;
    }
  });
  return nearest.candle.x;
}

export function chartPointToSvg(point, candles, priceToY) {
  if (!point || typeof priceToY !== "function") return null;
  const price = finiteNumber(point.price);
  const x = nearestCandleX(candles, point.timestamp);
  if (price === null || x === null) return null;
  const y = finiteNumber(priceToY(price));
  return y === null ? null : { x, y };
}

export function drawingToSvgPrimitive(drawing, geometry, chartWidth, priceHeight) {
  if (!drawing?.isVisible) return null;
  const tool = getDrawingTool(drawing.toolId);
  if (!tool || !Array.isArray(drawing.points)) return null;

  const candles = geometry?.candles || [];
  const priceToY = geometry?.priceToY;

  if (drawing.toolId === "trend-line") {
    if (drawing.points.length < 2) return null;
    const start = chartPointToSvg(drawing.points[0], candles, priceToY);
    const end = chartPointToSvg(drawing.points[1], candles, priceToY);
    if (!start || !end) return null;
    return {
      type: "line",
      x1: start.x,
      y1: start.y,
      x2: end.x,
      y2: end.y,
    };
  }

  if (drawing.toolId === "horizontal-line") {
    const price = finiteNumber(drawing.points[0]?.price);
    if (price === null || typeof priceToY !== "function") return null;
    const y = finiteNumber(priceToY(price));
    if (y === null) return null;
    return { type: "line", x1: 0, y1: y, x2: chartWidth, y2: y };
  }

  if (drawing.toolId === "vertical-line") {
    const x = nearestCandleX(candles, drawing.points[0]?.timestamp);
    if (x === null) return null;
    return { type: "line", x1: x, y1: 8, x2: x, y2: priceHeight + 8 };
  }

  return null;
}

export function svgCoordinateToChartPoint({ x, y }, geometry, priceHeight) {
  const candles = geometry?.candles || [];
  const maxPrice = finiteNumber(geometry?.maxPrice);
  const minPrice = finiteNumber(geometry?.minPrice);
  if (!candles.length || maxPrice === null || minPrice === null) return null;

  const visibleCandles = candles.filter(
    (candle) => finiteNumber(candle.x) !== null && candle.x >= 0
  );
  if (!visibleCandles.length) return null;

  let nearest = visibleCandles[0];
  visibleCandles.forEach((candle) => {
    if (Math.abs(candle.x - x) < Math.abs(nearest.x - x)) nearest = candle;
  });

  const clampedY = Math.min(priceHeight + 8, Math.max(8, y));
  const range = Math.max(maxPrice - minPrice, 0.0001);
  const price = maxPrice - ((clampedY - 8) / priceHeight) * range;

  return {
    timestamp: nearest.timestamp,
    price,
  };
}
